import { isDecimalString } from "@/shared/utils/decimal";

import { CURRENCY_SYMBOLS } from "../constants/dispersions.constants";

export const formatCurrency = (
  amount: number,
  _currency: string = "USD",
  locale: string = "es-CO",
): string => {
  return amount.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const parseCurrencyInput = (value: string): number | null => {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Parse a user-typed amount accepting BOTH es-CO ("1.500,50") and en-US ("1500.50") notations, plus the
 * dot-decimal strings the quote auto-fill writes back ("1500.5"). Mirrors the design prototype's `parse()`:
 * dots are treated as thousands separators ONLY when a comma is present. Returns NaN for empty/invalid
 * input.
 */
export function parseAmountInput(value: string | null | undefined): number {
  let v = (value ?? "").trim();
  if (!v) return NaN;
  if (v.includes(",")) v = v.replace(/\./g, "").replace(",", ".");
  return parseFloat(v);
}

/**
 * Decimal-string variant of `parseAmountInput` for exact comparisons against the runtime ledger amounts,
 * which are decimal strings that must never round-trip through `parseFloat` (docs/modules.md: no `Number`
 * arithmetic). Same notation tolerance ("1.500,50" / "1500.50" / "1500.5"); returns the normalized
 * dot-decimal string, or null for empty/invalid input.
 */
export function parseAmountInputDecimal(value: string | null | undefined): string | null {
  let v = (value ?? "").trim();
  if (!v) return null;
  if (v.includes(",")) v = v.replace(/\./g, "").replace(",", ".");
  return isDecimalString(v) ? v : null;
}

/**
 * Es-CO amount with exactly two decimals, NaN-safe ("1.500,50"). Single source for the wizard's display
 * numbers (step 1 card, rate bar, result screens) — previously three identical local copies.
 */
export function formatAmount2(n: number): string {
  return new Intl.NumberFormat("es-CO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
}

/**
 * `formatAmount2` for the runtime's decimal-string amounts: Intl formats the string directly (no `parseFloat`
 * round-trip, mirroring `ColocaAmountDisplay`), so 8-dp ledger balances keep exact precision. Malformed input
 * falls back to 0.
 */
export function formatDecimalAmount2(value: string, locale: string = "es-CO"): string {
  const trimmed = value.trim();
  const v: string | number = isDecimalString(trimmed) ? trimmed : 0;
  // Intl.NumberFormat v3 formats decimal strings exactly (no double-precision loss); the TS ES2022 lib
  // still types format() as number-only, hence the assertion (same as ColocaAmountDisplay).
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    // Grouping is stated rather than left to the locale, so this agrees with `ColocaAmountDisplay` and with
    // the grouped amount input whatever `locale` is passed. The default below is es-CO, whose own minimum is
    // one group and which therefore already separated `1.500` — but every caller in the send-money flow now
    // passes the reader's language, and `es` suppresses the separator under ten thousand. Without this the
    // same figure changed shape between the balance and the field above it the moment the locale stopped
    // being a constant.
    useGrouping: "always",
  }).format(v as number);
}

/** Get currency symbol for a given currency code */
export function getCurrencySymbol(currencyCode: string): string {
  return CURRENCY_SYMBOLS[currencyCode] || "$";
}

/** Format amount with currency symbol */
export function formatCurrencyWithSymbol(amount: number | string, currencyCode: string): string {
  const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  const symbol = getCurrencySymbol(currencyCode);

  return `${symbol}${numericAmount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Format country select option: "COP - Colombia" */
export function formatCountryOption(currency: string, name: string): string {
  return `${currency} - ${name}`;
}
